import { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import {
  Box,
  ButtonContainedLink,
  ButtonOutlinedLink,
  Card,
  CardAccount,
  Container,
  Information,
  LayoutButton,
  LayoutVertical,
  Typography,
  TypographyPoint,
} from "@/components";
import { Loader } from "@/components/Loader/Loader";
import { FetchContext as context } from "@/contexts/FetchContext";
import { ModalRegistered } from "@/features/common/Top/components";
import { useModal } from "@/hooks/useModal";
import type { MappingRequest } from "@/types/user";
import getPortalType from "@/utils/getPortalType";
import { AlertPointExpiring } from "../Points/components";
import { PointHistoryList } from "../PointHistory";
import { NotificationList } from "../Notifications";
import { path } from "@/path";

export const Top = () => {
  const { state } = useContext(context);
  const { t } = useTranslation();
  const { open, handleOpen, handleClose } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const portalType = getPortalType();

  // TODO 表示確認用のため適宜修正
  // biome-ignore lint/correctness/useExhaustiveDependencies: need not to watch
  useEffect(() => {
    handleOpen("modal-registered");
  }, []);

  const handleCloseModal = () => {
    handleClose();
    // モーダルの意図しない再描画を停止
    navigate(((p): string => (p ? `/${p}_portal/top` : "/"))(portalType));
  };

  return (
    <Container>
      {state.loading || !state.data?.user.driverCode ? (
        <Loader />
      ) : (
        <>
          <Helmet title={t("TOP")} />

          <LayoutVertical>
            {state.data.pointTransferMethod.bankAccountInfo?.failure && (
              <Information
                onClick={() => {
                  navigate(((p): string => (p ? `/${p}_portal/points/transfer/histories` : "/"))(portalType));
                }}
              >
                {t("POINT_RETURN_NOTICE_TITLE")}
              </Information>
            )}
            <CardAccount
              customerName={state.data.user.driverName}
              customerDriverScore={state.data.user.driverScore}
              infoMessage={
                state.data.user.mappingRequests.some(
                  (mappingRequest: MappingRequest) => mappingRequest.state === "unapplied"
                ) &&
                (state.data.user.mappingRequests.length > 1
                  ? t("COMBINED_APPLICATION_MORE")
                  : t("COMBINED_APPLICATION"))
              }
              enableWelcomeBack={true}
            >
              <LayoutButton sx={{ justifyContent: "flex-end" }}>
                <ButtonOutlinedLink to={((p): string => (p ? `/${p}_portal/account` : "/"))(portalType)}>
                  {t("SEE_MY_PAGE")}
                </ButtonOutlinedLink>
              </LayoutButton>
            </CardAccount>

            <Card>
              <Typography variant="h2" gutterBottom>
                {((p): string => (p ? t(`POINT_EXCHANGE_OF_GO_${p}`) : ""))(portalType.toUpperCase())}
              </Typography>
              <LayoutVertical>
                <Box textAlign="center">
                  <Typography variant="h3">{t("OWNED_POINT")}</Typography>
                  <TypographyPoint point={state.data.pointSummary.currentPoint} />
                </Box>
                <Box>
                  <AlertPointExpiring pointSummary={state.data.pointSummary} />
                  <LayoutButton>
                    <ButtonOutlinedLink
                      to={((p): string => (p ? `/${p}_portal/points` : "/"))(portalType)}
                      color="warning"
                      sx={{ px: "8px" }}
                    >
                      {t("SEE_EXPIRE_POINT")}
                    </ButtonOutlinedLink>
                  </LayoutButton>
                </Box>
                <LayoutButton>
                  <ButtonContainedLink
                    to={((p): string => (p ? `/${p}_portal/points/transfer` : "/"))(portalType)}
                    size="large"
                  >
                    {t("EXCHANGE_POINT")}
                    {state.data.pointTransferMethod.transferFee === 0 && (
                      <Typography variant="caption">{t("ONE_TIME_REMITTANCE_FEE_IS_WAIVED")}</Typography>
                    )}
                  </ButtonContainedLink>
                  <ButtonContainedLink
                    to={((p): string => (p ? `/${p}_portal/points/transfer/histories` : "/"))(portalType)}
                    color="secondary"
                    size="large"
                  >
                    {t("CHECK_APPLICATION_STATUS")}
                  </ButtonContainedLink>
                </LayoutButton>
              </LayoutVertical>
            </Card>
            <Card>
              <Typography variant="h2" gutterBottom>
                {t("EARNED_POINT")}
              </Typography>
              <LayoutVertical>
                <PointHistoryList pointHistories={state.data.pointHistories} isTop={true} />
                <LayoutButton>
                  <ButtonOutlinedLink to={((p): string => (p ? `/${p}_portal/points/history` : "/"))(portalType)}>
                    {t("SEE_POINT_HISTORY")}
                  </ButtonOutlinedLink>
                </LayoutButton>
              </LayoutVertical>
            </Card>

            <Card>
              <Typography variant="h2" gutterBottom>
                {t("NOTIFICATION")}
              </Typography>
              <NotificationList
                notices={state.data.notifications}
                detailUrl={portalType === "crew" ? path.crew.Notifications.detail : path.driver.Notifications.detail}
              />
              <LayoutButton>
                <ButtonOutlinedLink
                  to={portalType === "crew" ? path.crew.Notifications.root : path.driver.Notifications.root}
                >
                  {t("SEE_MORE_NOTIFICATION")}
                </ButtonOutlinedLink>
              </LayoutButton>
            </Card>
          </LayoutVertical>
        </>
      )}
      {location.state && location.state.status === "REGISTRATION_SUCCESS" && (
        <ModalRegistered open={open("modal-registered")} onClose={handleCloseModal} />
      )}
    </Container>
  );
};
